<script setup lang="ts">
import { linkGoOrder } from '@/event/basket/linkGoOrder'
import {
    showAlertAuthBasket,
    showAlertAuthBasketTransition
} from '@/happyTheme/event/modalBasket/showAlertValidateBasket'
import { toggleShowModalBasketHappy } from '@/happyTheme/utils/modalBasket/toggleShowModalBasketHappy'
import { useI18n } from 'vue-i18n'
import { LogIn, UserPlus, X, Lock } from 'lucide-vue-next'

const { t } = useI18n()

function toggleShowAuthBasketHappy() {
    showAlertAuthBasketTransition.value = false
    setTimeout(() => {
        showAlertAuthBasket.value = !showAlertAuthBasket.value
    }, 200)
}
function toggleLoginBasketHappy() {
    linkGoOrder.value = true
    setTimeout(() => {
        showAlertAuthBasketTransition.value = false
        setTimeout(() => {
            showAlertAuthBasket.value = !showAlertAuthBasket.value
            toggleShowModalBasketHappy()
        }, 200)
    }, 200)
}

function toggleRegisterBasketHappy() {
    setTimeout(() => {
        showAlertAuthBasketTransition.value = false
        setTimeout(() => {
            showAlertAuthBasket.value = !showAlertAuthBasket.value
            toggleShowModalBasketHappy()
        }, 200)
    }, 200)
}
</script>

<template>
    <section class="section-alert-validate-basket-happy">
        <section
            class="section-alert-validate-basket-overlay-happy"
            @click="toggleShowAuthBasketHappy()"
        ></section>

        <transition
            name="basket-alert-auth"
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <section
                class="alert-auth-card"
                v-if="showAlertAuthBasketTransition"
                role="dialog"
                aria-modal="true"
                :aria-label="t('loginToOrder')"
            >
                <!-- Üst accent şeridi -->
                <span class="alert-auth-topbar" aria-hidden="true"></span>

                <!-- Kapat -->
                <button
                    type="button"
                    class="alert-auth-close"
                    :aria-label="t('close')"
                    @click="toggleShowAuthBasketHappy()"
                >
                    <X class="w-4 h-4" :stroke-width="2.5" />
                </button>

                <!-- İkon chip -->
                <div class="alert-auth-icon-chip" aria-hidden="true">
                    <Lock class="w-6 h-6" :stroke-width="2" />
                </div>

                <!-- Başlık -->
                <p class="alert-auth-title">
                    {{ t('loginToOrder') }}
                </p>

                <!-- Butonlar -->
                <div class="alert-auth-actions">
                    <router-link
                        :to="{ name: 'login' }"
                        class="alert-auth-btn-primary"
                        @click="toggleLoginBasketHappy()"
                    >
                        <LogIn class="w-4 h-4" :stroke-width="2.25" />
                        <span>{{ t('login') }}</span>
                    </router-link>

                    <router-link
                        :to="{ name: 'register' }"
                        class="alert-auth-btn-secondary"
                        @click="toggleRegisterBasketHappy()"
                    >
                        <UserPlus class="w-4 h-4" :stroke-width="2.25" />
                        <span>{{ t('register') }}</span>
                    </router-link>
                </div>
            </section>
        </transition>
    </section>
</template>

<style scoped>
.section-alert-validate-basket-happy {
    @apply absolute inset-0 z-30 h-full w-full flex items-center justify-center;
}

.section-alert-validate-basket-overlay-happy {
    @apply absolute inset-0;
    @apply bg-text-dark-happy-light/50;
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
}

/* Kart */
.alert-auth-card {
    @apply relative w-[20rem] max-w-[calc(100%-1.5rem)] z-30 flex flex-col items-center
           rounded-2xl px-5 pt-8 pb-5 gap-4 overflow-hidden;
    @apply bg-t-card text-t-text ring-1 ring-t-border shadow-2xl;
    @apply happy-ifo-dark:ring-t-accent/25 happy-ifo-dark:shadow-black/40;
}

/* Üst accent şeridi */
.alert-auth-topbar {
    @apply absolute top-0 left-0 right-0 h-1;
    background: linear-gradient(
        90deg,
        rgb(var(--t-accent) / 0.6),
        rgb(var(--t-accent)),
        rgb(var(--t-accent) / 0.6)
    );
}

/* Kapat */
.alert-auth-close {
    @apply absolute top-2.5 right-2.5 w-7 h-7 rounded-full
           flex items-center justify-center cursor-pointer
           text-t-text-muted transition-colors duration-150;
}
.alert-auth-close:hover,
.alert-auth-close:focus {
    @apply text-t-text bg-t-border/40 outline-none;
}

/* İkon chip */
.alert-auth-icon-chip {
    @apply w-14 h-14 rounded-full flex items-center justify-center
           bg-t-accent/15 text-t-accent ring-1 ring-t-accent/30;
}

/* Başlık */
.alert-auth-title {
    @apply text-center font-semibold text-[0.95rem] leading-snug px-2;
}

/* Buton alanı */
.alert-auth-actions {
    @apply flex flex-col w-full gap-2 mt-1;
}

/* Primary CTA */
.alert-auth-btn-primary {
    @apply flex items-center justify-center gap-2
           font-semibold rounded-lg py-2.5 px-4 w-full
           bg-t-accent text-t-on-accent shadow-md
           transition-all duration-150;
}
.alert-auth-btn-primary:hover,
.alert-auth-btn-primary:focus {
    @apply bg-t-accent-hover shadow-lg outline-none;
    transform: translateY(-1px);
}
.alert-auth-btn-primary:active {
    @apply bg-t-accent-active shadow-sm;
    transform: translateY(0);
}

/* Secondary CTA */
.alert-auth-btn-secondary {
    @apply flex items-center justify-center gap-2
           font-medium rounded-lg py-2.5 px-4 w-full
           bg-transparent text-t-text ring-1 ring-t-border
           transition-all duration-150;
}
.alert-auth-btn-secondary:hover,
.alert-auth-btn-secondary:focus {
    @apply bg-t-border/25 ring-t-border-strong outline-none;
}
.alert-auth-btn-secondary:active {
    @apply bg-t-border/40;
}
</style>
